import { isGlobalLaunchUrl } from '@common/validation-schemas';
import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';
import {
  NEW_RELIC_BROWSER_ACCOUNT_ID,
  NEW_RELIC_BROWSER_AGENT_ID_VIEWER,
  NEW_RELIC_BROWSER_APPLICATION_ID_VIEWER,
  NEW_RELIC_BROWSER_LICENSE_KEY,
  NEW_RELIC_BROWSER_TRUST_KEY,
} from 'config/api/NewRelic';

import { postViewerLaunchMessage } from './viewerLaunchMessaging';

type LaunchPhase = 'launch_context' | 'applications';
interface DiagnosticAgent {
  addPageAction(name: string, attributes: Record<string, string | number | boolean>): void;
}
interface LaunchDetails {
  httpStatus?: number;
  orgId?: number;
  propertyId?: number;
  durationMs?: number;
  bypassRoutes?: boolean;
}
let agent: DiagnosticAgent | undefined;

function isGlobalLaunch(): boolean {
  const params = new URLSearchParams(window.location.search);
  return (
    params.get('launchRegion') === 'eu' ||
    [window.location.origin, params.get('parentOrigin'), document.referrer].some(isGlobalLaunchUrl)
  );
}

export function getLaunchDiagnosticQuery(): { launchRequestId?: string; launchRegion?: 'eu' } {
  const id = new URLSearchParams(window.location.search).get('launchRequestId') ?? '';
  return {
    ...(id.length === 16 && /^[a-f0-9]+$/i.test(id) ? { launchRequestId: id } : {}),
    ...(isGlobalLaunch() ? { launchRegion: 'eu' as const } : {}),
  };
}

export function getLaunchHttpStatus(error: unknown): number {
  const status = (error as { response?: { status?: unknown } } | null)?.response?.status;
  return typeof status === 'number' && Number.isInteger(status) && status >= 100 && status <= 599
    ? status
    : 0;
}

function getAgent(): DiagnosticAgent | undefined {
  const browser = window as Window & { newrelic?: DiagnosticAgent; NREUM?: DiagnosticAgent };
  if (browser.newrelic?.addPageAction) return browser.newrelic;
  if (browser.NREUM?.addPageAction) return browser.NREUM;
  if (!agent && NEW_RELIC_BROWSER_ACCOUNT_ID && !NEW_RELIC_BROWSER_ACCOUNT_ID.startsWith('%%')) {
    agent = new BrowserAgent({
      info: {
        beacon: 'bam.nr-data.net',
        errorBeacon: 'bam.nr-data.net',
        licenseKey: NEW_RELIC_BROWSER_LICENSE_KEY,
        applicationID: NEW_RELIC_BROWSER_APPLICATION_ID_VIEWER,
      },
      loader_config: {
        accountID: NEW_RELIC_BROWSER_ACCOUNT_ID,
        trustKey: NEW_RELIC_BROWSER_TRUST_KEY,
        agentID: NEW_RELIC_BROWSER_AGENT_ID_VIEWER,
        licenseKey: NEW_RELIC_BROWSER_LICENSE_KEY,
        applicationID: NEW_RELIC_BROWSER_APPLICATION_ID_VIEWER,
      },
      init: { privacy: { cookies_enabled: false } },
    });
  }
  return agent;
}

/** Telemetry adapter: allowlisted numbers/booleans only; never pass launch data or errors. */
export function recordLaunchFailure(phase: LaunchPhase, details: LaunchDetails = {}): void {
  const params = new URLSearchParams(window.location.search);
  const id = params.get('launchRequestId') ?? '';
  const ids = window.location.pathname.match(/^\/(\d+)\/(\d+)(?:\/|$)/);
  const attributes: Record<string, string | number | boolean> = {
    phase,
    outcome: 'failed',
    correlationId: id.length === 16 && /^[a-f0-9]+$/i.test(id) ? id : 'not_provided',
    region: isGlobalLaunch() ? 'eu' : 'us',
    embedded: window.parent !== window,
  };
  if (ids && Number.isSafeInteger(Number(ids[1])) && Number.isSafeInteger(Number(ids[2]))) {
    attributes.orgId = Number(ids[1]);
    attributes.propertyId = Number(ids[2]);
  }
  (['httpStatus', 'orgId', 'propertyId', 'durationMs'] as const).forEach((key) => {
    const value = details[key];
    if (value !== undefined && Number.isSafeInteger(value) && value >= 0) attributes[key] = value;
  });
  if (typeof details.bypassRoutes === 'boolean') attributes.bypassRoutes = details.bypassRoutes;
  let reported = false;
  try {
    const nrAgent = isGlobalLaunch() ? getAgent() : undefined;
    if (nrAgent) {
      nrAgent.addPageAction('LeasingJourneyLaunch', attributes);
      reported = true;
    }
  } catch {
    // Browser telemetry must never interrupt a launch or its fallback.
  }
  postViewerLaunchMessage({
    phase,
    outcome: 'failed',
    reported,
    httpStatus: typeof attributes.httpStatus === 'number' ? attributes.httpStatus : undefined,
  });
}
